import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import PageLayout from '@/components/layout/PageLayout'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiEmptyState } from '@/components/intueri/IntuiEmptyState'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiAlertDialog,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
  IntuiAlertDialogContent,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
} from '@/components/intueri/IntuiAlertDialog'
import {
  IntuiSelect,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectTrigger,
  IntuiSelectValue,
} from '@/components/intueri/IntuiSelect'
import {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
} from '@/components/intueri/IntuiTabs'
import { useAppStore } from '@/store'
import { Search, Plus, Trash, LayoutGrid, List, PanelsTopLeft, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { trackEvent } from '@/utils/analytics'
import { hasLandedOnFirstBoard, markLandedOnFirstBoard } from '@/utils/firstBoardLanding'
import BoardTableV2 from './BoardTableV2'
import BoardGridV2 from './BoardGridV2'

type ViewMode = 'list' | 'grid'
type SortBy = 'last_viewed_newest' | 'last_viewed_oldest' | 'alpha_az' | 'alpha_za'

const BOARD_PATH = '/board'

export default function BoardListV2() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const {
    loadBoardList,
    filteredBoardList,
    filterBoardList,
    createBoard,
    generateDefaultBoardName,
    deleteBoard,
    boardList,
    boardListTotal,
    boardListLoadingMore,
    loadMoreBoards,
    loading,
    setContextId,
    user,
  } = useAppStore()
  const { context_id } = useAppStore()

  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('last_viewed_newest')
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [hardDeleteTarget, setHardDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const init = async () => {
      await loadBoardList()
      setHasInitiallyLoaded(true)
    }
    init()
  }, [loadBoardList])

  // Auto-select context board. FS-553: no longer falls back to creating a
  // Master Board when the user has no boards — the empty state handles that
  // case and the user creates their first board explicitly.
  useEffect(() => {
    if (!hasInitiallyLoaded || context_id || boardList.length === 0) return
    const sorted = [...boardList].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )
    setContextId(sorted[0].id)
  }, [hasInitiallyLoaded, boardList, context_id, setContextId])

  // One-time landing: the first time a user arrives here, drop them straight
  // into their most recent board instead of the library. Every later visit shows
  // the library as before, so this never becomes a wall between the user and
  // their boards. Users who arrive via onboarding are already marked, since that
  // flow lands them on a board itself.
  useEffect(() => {
    if (!hasInitiallyLoaded || !user?.userId) return
    if (hasLandedOnFirstBoard(user.userId)) return

    // Nothing to land on. Deliberately NOT marked: an empty list also means
    // "the fetch failed" (loadBoardList swallows its error), and a transient
    // failure must not silently spend the user's one shot at this. The cost of
    // re-checking is a localStorage read, and once they do have a board they get
    // the landing they were owed.
    if (boardList.length === 0) return

    // Always the most recently updated board (the API returns `updatedAt DESC`),
    // regardless of the sort the user has chosen for the list — the intent is
    // "resume where you left off", not "open whatever is at the top of the grid".
    markLandedOnFirstBoard(user.userId)
    trackEvent('workspace-first_login_landing', { workspace_id: boardList[0].id })
    navigate(`${BOARD_PATH}/${boardList[0].id}`, { replace: true })
  }, [hasInitiallyLoaded, boardList, user?.userId, navigate])

  const goToBoard = (id: string) => {
    trackEvent('workspace-switched', { workspace_id: id })
    navigate(`${BOARD_PATH}/${id}`)
  }

  const handleCreateBoard = async () => {
    await loadBoardList()
    const defaultName = generateDefaultBoardName()
    const board = await createBoard(defaultName)
    if (board) {
      trackEvent('workspace-created', { workspace_id: board.id })
      goToBoard(board.id)
    }
  }

  const handleDeleteBoard = useCallback(async () => {
    if (!deleteTarget) return
    const success = await deleteBoard(deleteTarget.id)
    if (success) {
      trackEvent('workspace-deleted', { workspace_id: deleteTarget.id })
      toast.success(t('board:actions.delete_success', 'Board moved to archive'))
    }
    setDeleteTarget(null)
  }, [deleteTarget, deleteBoard, t])

  const handleHardDeleteBoard = useCallback(async () => {
    if (!hardDeleteTarget) return
    const success = await deleteBoard(hardDeleteTarget.id)
    if (success) {
      trackEvent('workspace-deleted', { workspace_id: hardDeleteTarget.id })
      toast.success(t('board:actions.hard_delete_success', 'Board deleted'))
    }
    setHardDeleteTarget(null)
  }, [hardDeleteTarget, deleteBoard, t])

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    filterBoardList(value)
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
    searchDebounceRef.current = setTimeout(() => {
      if (value.trim()) trackEvent('board-searched', { query_length: value.trim().length })
    }, 300)
  }

  const requestDelete = (id: string) => {
    const board = filteredBoardList.find((b) => b.id === id)
    setDeleteTarget({ id, name: board?.name || '' })
  }

  const requestHardDelete = (id: string) => {
    const board = filteredBoardList.find((b) => b.id === id)
    setHardDeleteTarget({ id, name: board?.name || '' })
  }

  // Sort boards
  // Infinite scroll, the same shape the data catalog uses (CatalogTableV2): a
  // sentinel below the last row, observed with a 200px rootMargin so the next page
  // is already in flight by the time the user reaches the bottom.
  //
  // `hasMore` is derived from the server's total rather than "the last page was
  // full", so a tenant whose board count divides exactly by the page size does not
  // fetch one extra empty page.
  const hasMore = boardListTotal !== null && boardList.length < boardListTotal
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!hasMore) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMoreBoards()
      },
      { rootMargin: '200px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasMore, loadMoreBoards])

  const sortedBoards = [...filteredBoardList].sort((a, b) => {
    if (sortBy === 'alpha_az') return a.name.localeCompare(b.name)
    if (sortBy === 'alpha_za') return b.name.localeCompare(a.name)
    if (sortBy === 'last_viewed_oldest') return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime()
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  })

  return (
    <PageLayout bgColor="bg-white dark:bg-dark-card" fullScreenContent>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative w-60">
            <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <IntuiInput
              placeholder={t('board:search_boards', 'Search')}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-9 ps-9 rounded-lg"
            />
          </div>

          {/* Sort */}
          <IntuiSelect value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
            <IntuiSelectTrigger className="w-[200px] h-9">
              <IntuiSelectValue />
            </IntuiSelectTrigger>
            <IntuiSelectContent>
              <IntuiSelectItem value="last_viewed_newest">{t('board:sort.last_viewed_newest', 'Newest first')}</IntuiSelectItem>
              <IntuiSelectItem value="last_viewed_oldest">{t('board:sort.last_viewed_oldest', 'Oldest first')}</IntuiSelectItem>
              <IntuiSelectItem value="alpha_az">{t('board:sort.alpha_az', 'Alphabetical (A→Z)')}</IntuiSelectItem>
              <IntuiSelectItem value="alpha_za">{t('board:sort.alpha_za', 'Alphabetical (Z→A)')}</IntuiSelectItem>
            </IntuiSelectContent>
          </IntuiSelect>
        </div>

        <div className="flex items-center gap-2">
          {/* Grid/List toggle */}
          <IntuiTabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <IntuiTabsList className="h-9">
              <IntuiTabsTrigger value="grid">
                <LayoutGrid className="size-4" />
              </IntuiTabsTrigger>
              <IntuiTabsTrigger value="list">
                <List className="size-4" />
              </IntuiTabsTrigger>
            </IntuiTabsList>
          </IntuiTabs>

          {/* Archive */}
          <IntuiButton variant="outline" size="lg" leftIcon={<Trash />} disabled>
            {t('board:actions.archive', 'Archive')}
          </IntuiButton>

          {/* New Board — data-tour-id: help-agent "intelligent-boards" tour
              anchor (always rendered, list or empty state). */}
          <IntuiButton
            size="lg"
            leftIcon={<Plus />}
            onClick={handleCreateBoard}
            loading={loading}
            data-tour-id="board-create"
          >
            {t('board:new_board', 'New Board')}
          </IntuiButton>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6">
        {sortedBoards.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            {searchTerm.trim() ? (
              <IntuiEmptyState
                variant="no-results"
                title={t('board:empty.no_results_title', 'No boards found')}
                description={t('board:empty.no_results_description', 'No boards match your search.')}
                className="w-[360px]"
              />
            ) : (
              <IntuiEmptyState
                variant="no-data"
                // Match the boards icon in the nav/topbar (nav-config.ts) rather
                // than the generic no-data folder.
                icon={<PanelsTopLeft className="size-4 text-muted-foreground" />}
                title={t('board:empty.title', 'No boards yet')}
                description={t('board:empty.description', 'Create your first board to get started.')}
                actions={
                  <IntuiButton leftIcon={<Plus />} onClick={handleCreateBoard} loading={loading}>
                    {t('board:empty.action', 'Create board')}
                  </IntuiButton>
                }
                className="w-[360px]"
              />
            )}
          </div>
        ) : viewMode === 'list' ? (
          <BoardTableV2
            boards={sortedBoards}
            onBoardClick={goToBoard}
            onBoardDelete={requestDelete}
            onBoardHardDelete={requestHardDelete}
          />
        ) : (
          <BoardGridV2
            boards={sortedBoards}
            onBoardClick={goToBoard}
            onBoardDelete={requestDelete}
            onBoardHardDelete={requestHardDelete}
          />
        )}

        {/* Infinite scroll: the sentinel only exists while pages remain, so the
            observer above tears itself down at the end of the list. */}
        {hasMore && <div ref={sentinelRef} className="h-1" />}
        {boardListLoadingMore && (
          <div className="flex justify-center py-4">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>

      {/* Archive confirmation dialog */}
      <IntuiAlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <IntuiAlertDialogContent>
          <IntuiAlertDialogHeader>
            <IntuiAlertDialogTitle>{t('board:actions.archive_title', 'Move board to archive')}</IntuiAlertDialogTitle>
            <IntuiAlertDialogDescription>
              {t('board:actions.archive_description', {
                name: deleteTarget?.name,
                defaultValue: `Are you sure? The board "${deleteTarget?.name}" will be moved to your archive. You can restore it later from your archive.`,
              })}
            </IntuiAlertDialogDescription>
          </IntuiAlertDialogHeader>
          <IntuiAlertDialogFooter>
            <IntuiAlertDialogCancel>{t('common:buttons.cancel', 'Cancel')}</IntuiAlertDialogCancel>
            <IntuiAlertDialogAction variant="destructive" onClick={handleDeleteBoard}>
              Archive
            </IntuiAlertDialogAction>
          </IntuiAlertDialogFooter>
        </IntuiAlertDialogContent>
      </IntuiAlertDialog>

      {/* Hard-delete confirmation dialog */}
      <IntuiAlertDialog open={!!hardDeleteTarget} onOpenChange={(open) => !open && setHardDeleteTarget(null)}>
        <IntuiAlertDialogContent>
          <IntuiAlertDialogHeader>
            <IntuiAlertDialogTitle>{t('board:actions.delete_title', 'Delete board permanently')}</IntuiAlertDialogTitle>
            <IntuiAlertDialogDescription>
              {t('board:actions.delete_description', {
                name: hardDeleteTarget?.name,
                defaultValue: `Are you sure? The board "${hardDeleteTarget?.name}" will be permanently deleted. This action cannot be undone.`,
              })}
            </IntuiAlertDialogDescription>
          </IntuiAlertDialogHeader>
          <IntuiAlertDialogFooter>
            <IntuiAlertDialogCancel>{t('common:buttons.cancel', 'Cancel')}</IntuiAlertDialogCancel>
            <IntuiAlertDialogAction variant="destructive" onClick={handleHardDeleteBoard}>
              {t('board:actions.delete', 'Delete')}
            </IntuiAlertDialogAction>
          </IntuiAlertDialogFooter>
        </IntuiAlertDialogContent>
      </IntuiAlertDialog>
    </PageLayout>
  )
}
